import { useSignal } from "@preact/signals-react";
import { useEffect, useState } from "react";
import { ErrorField } from "../../../../types/ErrorField";
import TextEdit from "../../../molecules/TextEdit";
import SelectTextField from "../../../molecules/SelectTextField";
import { Auth } from 'aws-amplify';

import { toast } from "react-toastify";

import ATable from "../../../organisms/ATable";
import { ATableHeader } from "../../../organisms/ATable";
import { FaStar } from "react-icons/fa";
import NewTooltip from "../../../atoms/NewTooltip";
import { HiOutlineTrash } from "react-icons/hi2";

import PresentationModal from "../../../organisms/PresentationModal";
import { getErrorMessage } from "../../../../utils/formErrorUtils";
import useCities from "../../../../hooks/useCities";
import MiniLoading from "../../../ions/MiniLoading";
import { useNavigate } from "react-router-dom";

interface BankAccount {
    id?: string;
    bankAccountId?: string;
    bank: string;
    cityId: number;
    city?: string;
    agency: string;
    account: string;
    active: boolean;
    canRemove: boolean;
    canSetActive: boolean;
    default: boolean;
}

interface Bank {
    id: number;
    name: string;
    febraban_id: string;
}

interface Props {
    personId: string;
    personType: 'individual' | 'legal_person';
    provinces: any[];
    banks: Bank[];
    bankAccounts: BankAccount[];
    onBankAccountChange?: () => void; // ✅ Callback para notificar mudanças
}

type BankAccountForm = {
    bankId: string;
    cityId: number;
    agency: string;
    account: string;
    digit: string;
    active: boolean;
    province: string;
};

const API_KEY = process.env.REACT_APP_API_KEY || '';

function PersonFormBankAccount({ personId, personType, provinces, banks, bankAccounts, onBankAccountChange }: Props): JSX.Element {
    // ✅ NOVO: API endpoint unificado para person
    const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/person`;
    
    const isModalOpen = useSignal<boolean>(false);
    const { fetchCities } = useCities();
    const navigate = useNavigate();

    const cities = useSignal<any[]>([]);
    const isLoadingForm = useSignal<boolean>(false);
    const [rows, setRows] = useState<BankAccount[]>(bankAccounts);

    // ✅ Atualizar rows quando bankAccounts prop mudar - FILTRAR APENAS ATIVAS
    useEffect(() => {
        // 🔍 FILTRO: Mostrar apenas contas ativas (para DELETE aparecer como remoção)
        const activeBankAccounts = bankAccounts.filter(account => account.active !== false);
        setRows(activeBankAccounts);
    }, [bankAccounts]);

    const isSendingForm = useSignal<boolean>(false);
    const isRemoveModalOpen = useSignal<boolean>(false);
    const confirmBankAccountNumber = useSignal<string>("");
    const selectedBankAccount = useSignal<BankAccount | null>(null);
    const errorFields = useSignal<ErrorField[]>([]);

    const accountForm = useSignal<BankAccountForm>({
        bankId: "",
        cityId: 0,
        agency: "",
        account: "",
        digit: "",
        active: true,
        province: "",
    });

    const resetForm = () => {
        accountForm.value = {
            bankId: "",
            cityId: 0,
            agency: "",
            account: "",
            digit: "",
            active: true,
            province: "",
        };
        errorFields.value = [];
    };

    const headers: ATableHeader[] = [
        {
            id: 'bank',
            title: 'Banco',
            canSort: true,
            field: 'bank',
            width: '200px',
            cssClass: 'w-[200px]',
        },
        {
            id: 'city',
            title: 'Cidade',
            canSort: true,
            field: 'city',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'agency',
            title: 'Agência',
            canSort: true,
            field: 'agency',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'account',
            title: 'Conta',
            canSort: true,
            field: 'account',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'active',
            title: 'Ativa',
            canSort: false,
            field: 'active',
            width: '90px',
            cssClass: 'w-[90px]',
        },
        {
            id: 'action',
            title: '',
            canSort: false,
            width: '50px',
        },
    ];

    useEffect(() => {
        window.addEventListener("BankAccountFormTab:openModal", () => isModalOpen.value = true);

        return () => {
            window.removeEventListener("BankAccountFormTab:openModal", () => isModalOpen.value = true);
        };
    }, []);

    const handleInputChange = (field: keyof BankAccountForm, value: string | boolean) => {
        accountForm.value = {
            ...accountForm.value,
            [field]: value,
        };
    };
    
    const onlyDigits = (value: string) => value.replace(/\D/g, "");
    const onlyDigitOrX = (value: string) => value.replace(/[^0-9xX]/g, "").toUpperCase();
    
    const submitForm = async () => {
        isSendingForm.value = true;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${personId}/bank-accounts`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...accountForm.value,
                    cityId: parseInt(accountForm.value.cityId as unknown as string),
                }),
            });

            if (response.ok) {
                toast.success('Conta bancária cadastrada com sucesso!');
                isModalOpen.value = false;
                deactivateAllRows();
                
                // ✅ Notificar componente pai para recarregar dados
                if (onBankAccountChange) {
                    onBankAccountChange();
                }
                const data = await response.json();
                createAccountRow(data);
                resetForm();
            } else if (response.status === 401) {
                toast.error('A sessão expirou. Faça login novamente.');
                navigate('/login');
            } else if (response.status === 409) {
                const { errors, message } = await response.json();
                toast.error(message);
                errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
            } else {
                toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
            }
        } catch (error) {
            toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        }
        isSendingForm.value = false;
    };

    const createAccountRow = (data: any) => {
        console.log('🔍 createAccountRow chamada com data:', data);
        console.log('🔍 accountForm.value.cityId:', accountForm.value.cityId);
        console.log('🔍 cities.value:', cities.value);
        console.log('🔍 accountForm.value.province:', accountForm.value.province);
        
        const bank = banks.find((bank) => bank.id === data.bank_id);
        const city = cities.value.find((city) => city.id === accountForm.value.cityId);
        const province = provinces.find((province) => province.initials === accountForm.value.province);

        console.log('🔍 bank encontrado:', bank);
        console.log('🔍 city encontrada:', city);
        console.log('🔍 province encontrada:', province);

        const newRow = {
            bankAccountId: data.id,
            agency: data.agency,
            account: `${data.account}-${data.digit}`,
            cityId: data.city_id,
            city: city?.name || '',
            bank: bank?.name || '',
            active: true,
            canRemove: true,
            canSetActive: false,
            default: false,
        };
        
        console.log('🔍 newRow criada:', newRow);
        console.log('🔍 rows antes:', rows);
        
        setRows([...rows, newRow]);
        
        console.log('🔍 rows depois:', [...rows, newRow]);
    };

    const deactivateAllRows = () => {
        setRows(rows.map(row => ({
            ...row,
            active: false,
            canSetActive: true,
        })));
    };

    // ✅ NOVA FUNÇÃO: Toggle ativo/inativo
    const toggleAccountActivation = async (bankAccountId: string, currentStatus: boolean) => {
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            
            const response = await fetch(`${BASE_URL}/${personId}/bank-account-toggle`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ bankAccountId }),
            });

            if (response.ok) {
                const action = currentStatus ? 'desativada' : 'ativada';
                toast.success(`Conta bancária ${action} com sucesso!`);
                
                // Atualizar estado local
                setRows(rows.map(row =>
                    row.bankAccountId === bankAccountId
                        ? { ...row, active: !currentStatus, canSetActive: currentStatus }
                        : row
                ));
                
                if (onBankAccountChange) {
                    onBankAccountChange();
                }
            } else {
                toast.error("Erro ao alterar status da conta bancária!");
            }
        } catch (error) {
            toast.error("Erro ao alterar status da conta bancária!");
        }
    };

    const deactivateAccount = async (bankAccountId: string) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${personId}/bank-accounts/${bankAccountId}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                toast.success('Conta bancária desativada com sucesso!');
                setRows(rows.map(row =>
                    row.bankAccountId === bankAccountId
                        ? { ...row, active: false, canSetActive: true }
                        : row
                ));
                
                if (onBankAccountChange) {
                    onBankAccountChange();
                }
            } else {
                toast.error("Erro ao desativar conta bancária!");
            }
        } catch (error) {
            toast.error("Erro ao desativar conta bancária!");
        }
    };



    const removeAccount = async () => {
        if (!selectedBankAccount.value) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${personId}/bank-accounts/${selectedBankAccount.value.bankAccountId}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                toast.success('Conta bancária removida com sucesso!');
                setRows(rows.filter(row => row.bankAccountId !== selectedBankAccount.value?.bankAccountId));
                isRemoveModalOpen.value = false;
                selectedBankAccount.value = null;
                confirmBankAccountNumber.value = "";
                
                // ✅ Notificar componente pai para recarregar dados
                if (onBankAccountChange) {
                    onBankAccountChange();
                }
            } else {
                toast.error("Erro ao remover conta bancária!");
            }
        } catch (error) {
            toast.error("Erro ao remover conta bancária!");
        }
    };

    const openRemoveModal = (bankAccount: BankAccount) => {
        selectedBankAccount.value = bankAccount;
        isRemoveModalOpen.value = true;
    };

    const renderRows = (data: BankAccount[]) => {
        return data.map((row, index) => (
            <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} hover:bg-slate-300 hover:shadow-md border-b border border-gray-300`}>
                <td className="py-2 px-4">{row.bank}</td>
                <td className="py-2 px-4">{row.city}</td>
                <td className="py-2 px-4">{row.agency}</td>
                <td className="py-2 px-4">{row.account}</td>
                <td className="py-2 px-4 text-center">
                    {/* ✅ NOVO: Estrela sempre clicável para toggle */}
                    <NewTooltip content={row.active ? "Desativar conta" : "Ativar conta"}>
                        <button
                            id={`btn-toggle-bank-${row.bankAccountId}`}
                            onClick={() => toggleAccountActivation(row.bankAccountId!, row.active)}
                            className={row.active ? "text-yellow-500 hover:text-gray-400" : "text-gray-400 hover:text-yellow-500"}
                        >
                            <FaStar />
                        </button>
                    </NewTooltip>
                </td>
                <td className="py-2 px-4 text-center">
                    {row.canRemove && (
                        <NewTooltip content="Remover conta">
                            <button
                                id={`btn-remove-bank-${row.bankAccountId}`}
                                onClick={() => openRemoveModal(row)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <HiOutlineTrash />
                            </button>
                        </NewTooltip>
                    )}
                </td>
            </tr>
        ));
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Contas Bancárias</h3>
                <button
                    id="btn-add-bank-account"
                    onClick={() => isModalOpen.value = true}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Nova conta bancária
                </button>
            </div>

            <ATable headers={headers}>
                {rows.length > 0 ? (
                    renderRows(rows)
                ) : (
                    <tr>
                        <td colSpan={headers.length} className="text-center py-4 text-gray-500">
                            Nenhuma conta bancária cadastrada
                        </td>
                    </tr>
                )}
            </ATable>

            {/* Modal de Nova Conta */}
            <PresentationModal
                id="bank-account-modal"
                show={isModalOpen.value}
                onClose={() => {
                    isModalOpen.value = false;
                    resetForm();
                }}
                header="Nova Conta Bancária"
            >
                <div className="p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <SelectTextField
                            id="bank-select"
                            label="Banco"
                            value={accountForm.value.bankId}
                            onChange={(e) => handleInputChange('bankId', e.target.value)}
                            items={(banks || []).map(bank => ({ value: bank.id.toString(), label: bank.name }))}
                            errorMessage={getErrorMessage(errorFields.value, 'bankId')}
                        />
                        
                        <SelectTextField
                            id="province"
                            label="UF"
                            value={accountForm.value.province}
                            onChange={async (e) => {
                                const value = e.target.value;
                                handleInputChange('province', value);
                                isLoadingForm.value = true;
                                const citiesData = await fetchCities(value);
                                cities.value = citiesData;
                                isLoadingForm.value = false;
                            }}
                            items={(provinces || []).map(province => ({ value: province.initials, label: province.initials }))}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="relative">
                            <SelectTextField
                                id="cityId"
                                label="Cidade"
                                value={accountForm.value.cityId.toString()}
                                onChange={(e) => handleInputChange('cityId', e.target.value)}
                                items={cities.value.map(city => ({ value: city.id.toString(), label: city.name }))}
                                disabled={isLoadingForm.value}
                            />
                            {isLoadingForm.value && (
                                <div className="absolute right-2 top-8">
                                    <MiniLoading />
                                </div>
                            )}
                        </div>

                        <TextEdit
                            id="agency"
                            label="Agência"
                            value={accountForm.value.agency}
                            onChange={(e) => handleInputChange('agency', onlyDigits(e.target.value))}
                            errorMessage={getErrorMessage(errorFields.value, 'agency')}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <TextEdit
                            id="account"
                            label="Conta Corrente"
                            value={accountForm.value.account}
                            onChange={(e) => handleInputChange('account', onlyDigits(e.target.value))}
                            errorMessage={getErrorMessage(errorFields.value, 'account')}
                        />

                        <TextEdit
                            id="account-digit"
                            label="Dígito"
                            value={accountForm.value.digit}
                            onChange={(e) => handleInputChange('digit', onlyDigitOrX(e.target.value))}
                            errorMessage={getErrorMessage(errorFields.value, 'digit')}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            id="btn-cancel-bank-account"
                            onClick={() => {
                                isModalOpen.value = false;
                                resetForm();
                            }}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Cancelar
                        </button>
                        <button
                            id="btn-save-bank-account"
                            onClick={submitForm}
                            disabled={isSendingForm.value}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {isSendingForm.value ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </PresentationModal>

            {/* Modal de Confirmação de Remoção */}
            <PresentationModal
                id="remove-bank-account-modal"
                show={isRemoveModalOpen.value}
                onClose={() => {
                    isRemoveModalOpen.value = false;
                    selectedBankAccount.value = null;
                    confirmBankAccountNumber.value = "";
                }}
                header="Confirmar Remoção"
            >
                <div className="p-4">
                    <p className="mb-4">
                        Para confirmar a remoção da conta bancária, digite o número da conta:
                    </p>
                    <p className="font-semibold mb-4">
                        {selectedBankAccount.value?.account}
                    </p>
                    
                    <TextEdit
                        id="confirm-account-number"
                        label="Confirme o número da conta"
                        value={confirmBankAccountNumber.value}
                        onChange={(e) => confirmBankAccountNumber.value = e.target.value}
                    />

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            id="btn-cancel-remove-bank"
                            onClick={() => {
                                isRemoveModalOpen.value = false;
                                selectedBankAccount.value = null;
                                confirmBankAccountNumber.value = "";
                            }}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Cancelar
                        </button>
                        <button
                            id="btn-confirm-remove-bank"
                            onClick={removeAccount}
                            disabled={confirmBankAccountNumber.value !== selectedBankAccount.value?.account}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                        >
                            Confirmar Remoção
                        </button>
                    </div>
                </div>
            </PresentationModal>
        </div>
    );
}

export default PersonFormBankAccount;
